import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, FileText, Plus } from 'lucide-react';
import { BadgeAlt, Button, Popover, PopoverContent, PopoverTrigger, } from '@cargomatic/ui';
import { getCurrentDeliveryOrder, getDocKey } from 'utils/documents';
import { getDocumentsV2 } from 'utils/DocumentsAPIUtils';
import useSignedUrl from 'utils/hooks/useSignedUrl';
import { carrierViewableDocTypesMap, shipperViewableDocTypesMap, } from 'config/documents';
import SessionStore from 'stores/SessionStore';
import { DocumentUploadModal } from 'components/modules/shipments/tracking/DocumentUploadModal';
const TYPE_BADGE = {
    bill_of_lading: {
        label: 'BOL',
        className: 'bg-blue-100 text-blue-800 border-blue-300',
    },
    customer_bill_of_lading: {
        label: 'CBOL',
        className: 'bg-sky-100 text-sky-800 border-sky-300',
    },
    proof_of_delivery: {
        label: 'POD',
        className: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    },
    rate_confirmation: {
        label: 'RC',
        className: 'bg-purple-100 text-purple-800 border-purple-300',
    },
    invoice: {
        label: 'INV',
        className: 'bg-amber-100 text-amber-800 border-amber-300',
    },
    delivery_order: {
        label: 'DO',
        className: 'bg-teal-100 text-teal-800 border-teal-300',
    },
};
function badgeFor(type) {
    return (TYPE_BADGE[type] || {
        label: type.slice(0, 3).toUpperCase(),
        className: 'bg-gray-100 text-gray-800 border-gray-300',
    });
}
function keepLatestDeliveryOrder(docs) {
    // getDocumentsV2 rows carry the document type on `type` (`key` is the
    // signed-url storage key), but getCurrentDeliveryOrder selects via
    // `{ key: 'delivery_order', show }`. Normalize to that contract before
    // collapsing, then map the winner back to its original row.
    const normalized = docs.map((doc) => {
        var _a;
        return (Object.assign(Object.assign({}, doc), { key: doc.type, show: (_a = doc.show) !== null && _a !== void 0 ? _a : true, created_at: doc.createdAt, original: doc }));
    });
    const latest = getCurrentDeliveryOrder(normalized);
    if (latest) {
        return docs
            .filter((doc) => doc.type !== 'delivery_order')
            .concat([latest.original]);
    }
    return docs;
}
export function DocumentsCell({ shipment }) {
    const { t } = useTranslation();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [open, setOpen] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    // Single-flight guard: `hasFetched` only flips after the await resolves, so a
    // quick close/reopen could fire a second getDocumentsV2 for the same row.
    const inFlightRef = useRef(false);
    const { fetchAndOpen } = useSignedUrl();
    const userRole = useMemo(() => {
        var _a;
        const u = (_a = SessionStore.getUser) === null || _a === void 0 ? void 0 : _a.call(SessionStore);
        return (u && u._type) || null;
    }, []);
    // Lazy: never fetch on mount (the cell renders once per row). The document
    // list is loaded only when the popover is first opened, and each document's
    // signed URL is fetched only when that row is clicked (see fetchAndOpen).
    const fetchDocs = useCallback(async () => {
        var _a, _b, _c;
        if (!(shipment === null || shipment === void 0 ? void 0 : shipment._id) || inFlightRef.current)
            return;
        inFlightRef.current = true;
        setLoading(true);
        try {
            const res = (await getDocumentsV2([shipment._id]));
            // getDocumentsV2 resolves `{ errors }` on failure instead of throwing.
            // Only mark the cell as fetched on success — a transient error must not
            // cache an empty "No documents" state; reopening the popover retries.
            if ((_a = res.errors) === null || _a === void 0 ? void 0 : _a.length)
                return;
            setDocs(((_c = (_b = res.data) === null || _b === void 0 ? void 0 : _b.getDocumentsV2) === null || _c === void 0 ? void 0 : _c.documents) || []);
            setHasFetched(true);
        }
        catch (_d) {
            // Same contract as the `{ errors }` path: leave hasFetched false so the
            // next popover open retries.
        }
        finally {
            setLoading(false);
            inFlightRef.current = false;
        }
    }, [shipment === null || shipment === void 0 ? void 0 : shipment._id]);
    const handleOpenChange = useCallback((next) => {
        setOpen(next);
        if (next && !hasFetched)
            fetchDocs();
    }, [hasFetched, fetchDocs]);
    const scopedDocs = useMemo(() => {
        const filtered = docs.filter((doc) => {
            if (userRole === 'Admin')
                return true;
            const docType = getDocKey(doc.type);
            if (userRole === 'Shipper')
                return !!shipperViewableDocTypesMap[docType];
            if (userRole === 'Carrier')
                return !!carrierViewableDocTypesMap[docType];
            // Unknown/null role: deny by default rather than leaking carrier-viewable
            // document names to an unrecognized `_type`.
            return false;
        });
        return keepLatestDeliveryOrder(filtered);
    }, [docs, userRole]);
    const count = scopedDocs.length;
    return (React.createElement("div", { className: "flex items-center gap-1" },
        React.createElement(Popover, { modal: false, open: open, onOpenChange: handleOpenChange },
            React.createElement(PopoverTrigger, { asChild: true },
                React.createElement(Button, { variant: "outline", size: "sm", className: "h-8 px-2 gap-1.5", onClick: (e) => e.stopPropagation() },
                    React.createElement(FileText, { className: "size-3.5 text-muted-foreground" }),
                    React.createElement("span", { className: "text-xs font-medium" }, hasFetched ? `${count} docs` : 'Docs'),
                    open ? (React.createElement(ChevronUp, { className: "size-3.5 text-muted-foreground" })) : (React.createElement(ChevronDown, { className: "size-3.5 text-muted-foreground" })))),
            React.createElement(PopoverContent, { className: "w-72 p-0", align: "start", onClick: (e) => e.stopPropagation() },
                React.createElement("div", { className: "px-3 py-2 border-b" },
                    React.createElement("p", { className: "text-xs font-semibold text-muted-foreground tracking-wide" },
                        "DOCUMENTS (",
                        count,
                        ")")),
                React.createElement("ul", { className: "max-h-72 overflow-y-auto py-1" },
                    loading && (React.createElement("li", { className: "px-3 py-2 text-sm text-muted-foreground" }, "Loading\u2026")),
                    !loading && scopedDocs.length === 0 && (React.createElement("li", { className: "px-3 py-2 text-sm text-muted-foreground" }, "No documents")),
                    !loading &&
                        scopedDocs.map((doc) => {
                            const badge = badgeFor(getDocKey(doc.type));
                            return (React.createElement("li", { key: doc._id },
                                React.createElement("button", { type: "button", onClick: () => fetchAndOpen({ documentKey: doc.key }), className: "w-full flex items-center gap-2 px-3 py-1.5 hover:bg-muted text-left" },
                                    React.createElement(BadgeAlt, { variant: "outline", className: `shrink-0 text-xs font-mono ${badge.className}` }, badge.label),
                                    React.createElement("span", { className: "text-sm truncate" }, doc.name))));
                        })),
                React.createElement("button", { type: "button", onClick: () => {
                        setOpen(false);
                        setShowUploadModal(true);
                    }, className: "w-full flex items-center gap-2 px-3 py-2 border-t hover:bg-muted text-emerald-700" },
                    React.createElement(Plus, { className: "size-4" }),
                    React.createElement("span", { className: "text-sm font-medium" }, "Add document")))),
        React.createElement(Button, { variant: "ghost", size: "sm", className: "h-8 w-8 p-0", "aria-label": "Add document", onClick: (e) => {
                e.stopPropagation();
                // Close the popover first so the modal doesn't open on top of a
                // still-open popover (the footer CTA already does this).
                setOpen(false);
                setShowUploadModal(true);
            } },
            React.createElement(Plus, { className: "size-4 text-emerald-600" })),
        showUploadModal && (React.createElement(DocumentUploadModal, { show: showUploadModal, onHide: () => setShowUploadModal(false), onUpload: fetchDocs, user: userRole, shipment: shipment, t: t }))));
}
